import { useState } from "react";
import { colorForFolderId } from "../lib/folders/folderColor.js";
import ConfirmDialog from "../Components/ConfirmDialog.js";
import FolderIcon from "./FolderIcon.js";
import "./FolderRail.css";

/**
 * Folder navigation + management, replacing the old inline chip list.
 * Pinned "All"/"Unfiled" always come first (neutral styling, not a
 * palette color, so they read as structural rather than "just another
 * folder") - "All" is the caller's default activeFolder on first load.
 *
 * Real folders below are searchable and live in a height-capped,
 * scrollable grid - browsable for a handful, searchable directly once
 * there are many, without switching to a different layout entirely.
 *
 * Each folder's color is derived, not stored (colorForFolderId, keyed
 * on the folder's own id) - no backend change needed for color-coding
 * at all.
 *
 * Rename/delete moved from the old chips' inline text links to a cog
 * icon per tile, opening one small "manage" dialog (name field for
 * rename, a Delete button below it) - delete itself routes through the
 * existing ConfirmDialog for a deliberate second step, matching how
 * asset deletion already works on this same page.
 *
 * onCreateFolder/onRenameFolder/onDeleteFolder are expected to return a
 * Promise that rejects with a real Error on failure (matching
 * lib/folders/folders.js's existing contract) - this component surfaces
 * that message in the dialog rather than assuming success.
 *
 * readOnly hides the cog and "+ New folder" tile entirely (the brand's
 * own Assets tab - folder management stays on the global Assets page).
 */
export default function FolderRail({
  folders,
  assets,
  activeFolder,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  readOnly = false,
}) {
  const [search, setSearch] = useState("");
  // The folder object being managed (has a real id), or {id: null} as
  // the create-mode sentinel - null means the dialog is closed.
  const [managingFolder, setManagingFolder] = useState(null);
  const [dialogName, setDialogName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const list = folders ?? [];
  const totalCount = (assets ?? []).length;
  const unfiledCount = (assets ?? []).filter((a) => !a.folderId).length;
  const countFor = (folderId) => (assets ?? []).filter((a) => a.folderId === folderId).length;

  const term = search.trim().toLowerCase();
  const filtered = term ? list.filter((f) => f.name.toLowerCase().includes(term)) : list;

  function openManage(folder) {
    setManagingFolder(folder);
    setDialogName(folder.name);
    setDialogError(null);
    setConfirmingDelete(false);
  }

  function openCreate() {
    setManagingFolder({ id: null, name: "" });
    setDialogName("");
    setDialogError(null);
  }

  function closeDialog() {
    setManagingFolder(null);
    setConfirmingDelete(false);
  }

  async function handleSave() {
    const name = dialogName.trim();
    if (!name) return;
    setIsSaving(true);
    setDialogError(null);
    try {
      if (managingFolder.id) {
        await onRenameFolder(managingFolder.id, name);
      } else {
        await onCreateFolder(name);
      }
      closeDialog();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    setIsSaving(true);
    setDialogError(null);
    try {
      await onDeleteFolder(managingFolder);
      closeDialog();
    } catch (err) {
      // Stay on the manage dialog with the real error (e.g. the
      // backend's own "still has assets in it" message) rather than
      // silently closing on a failed delete.
      setConfirmingDelete(false);
      setDialogError(err.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="folder-rail">
      <div className="folder-rail__header">
        <p className="folder-rail__title">Folders</p>
        {list.length > 6 ? (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search folders…"
            aria-label="Search folders"
            className="folder-rail__search"
          />
        ) : null}
      </div>

      <div className="folder-rail__scroll">
        <div className="folder-rail__grid">
          <button
            type="button"
            className={`folder-tile folder-tile--pinned${activeFolder === "all" ? " folder-tile--active" : ""}`}
            onClick={() => onSelectFolder("all")}
          >
            <FolderIcon />
            <div className="folder-tile__body">
              <p className="folder-tile__name">All</p>
              <p className="folder-tile__count">
                {totalCount} banner{totalCount === 1 ? "" : "s"}
              </p>
              {activeFolder === "all" ? <ActiveBadge /> : null}
            </div>
          </button>
          <button
            type="button"
            className={`folder-tile folder-tile--pinned folder-tile--unfiled${activeFolder === "unfiled" ? " folder-tile--active" : ""}`}
            onClick={() => onSelectFolder("unfiled")}
          >
            <FolderIcon />
            <div className="folder-tile__body">
              <p className="folder-tile__name">Unfiled</p>
              <p className="folder-tile__count">
                {unfiledCount} banner{unfiledCount === 1 ? "" : "s"}
              </p>
              {activeFolder === "unfiled" ? <ActiveBadge /> : null}
            </div>
          </button>

          {filtered.length === 0 && term ? (
            <p className="folder-rail__no-match">No folders match &ldquo;{search}&rdquo;.</p>
          ) : (
            filtered.map((folder) => {
              const color = colorForFolderId(folder.id);
              const count = countFor(folder.id);
              return (
                <button
                  key={folder.id}
                  type="button"
                  className={`folder-tile${activeFolder === folder.id ? " folder-tile--active" : ""}`}
                  style={{ "--folder-color": color.color, "--folder-color-soft": color.soft }}
                  onClick={() => onSelectFolder(folder.id)}
                >
                  <FolderIcon />
                  <div className="folder-tile__body">
                    {!readOnly ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className="folder-tile__cog"
                        aria-label={`Manage ${folder.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openManage(folder);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            openManage(folder);
                          }
                        }}
                      >
                        ⚙
                      </span>
                    ) : null}
                    <p className="folder-tile__name">{folder.name}</p>
                    <p className="folder-tile__count">
                      {count} banner{count === 1 ? "" : "s"}
                    </p>
                    {activeFolder === folder.id ? <ActiveBadge /> : null}
                  </div>
                </button>
              );
            })
          )}
          {!readOnly && !term ? (
            <button type="button" className="folder-tile folder-tile--new" onClick={openCreate}>
              <div className="folder-tile__body">
                <span>+ New folder</span>
              </div>
            </button>
          ) : null}
        </div>
      </div>

      {managingFolder ? (
        <div className="folder-dialog__scrim" onClick={closeDialog}>
          <div
            className="folder-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={managingFolder.id ? `Manage ${managingFolder.name}` : "New folder"}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="folder-dialog__title">{managingFolder.id ? "Manage folder" : "New folder"}</h3>
            <label className="folder-dialog__field">
              <span>Name</span>
              <input
                type="text"
                value={dialogName}
                onChange={(e) => setDialogName(e.target.value)}
                disabled={isSaving}
                autoFocus
              />
            </label>
            {dialogError ? <p className="folder-dialog__error">{dialogError}</p> : null}
            <div className="folder-dialog__actions">
              <button type="button" className="folder-dialog__cancel" onClick={closeDialog} disabled={isSaving}>
                Cancel
              </button>
              <button
                type="button"
                className="folder-dialog__save"
                onClick={handleSave}
                disabled={isSaving || !dialogName.trim()}
              >
                {isSaving ? "Saving…" : "Save"}
              </button>
            </div>
            {managingFolder.id ? (
              <button
                type="button"
                className="folder-dialog__delete"
                onClick={() => setConfirmingDelete(true)}
                disabled={isSaving}
              >
                Delete folder
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {confirmingDelete ? (
        <ConfirmDialog
          title={`Delete "${managingFolder.name}"?`}
          message="This can't be undone."
          confirmLabel="Delete"
          danger
          isBusy={isSaving}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * The active/selected indicator - a small checkmark badge. Deliberately
 * a normal flex child placed after the name/count text (not position:
 * absolute with hardcoded coordinates): .folder-tile__body is already a
 * flex column, so this reliably lands directly below the text without
 * needing to be measured against the icon's own geometry, and can never
 * collide with .folder-tile__cog (which IS absolutely positioned, in
 * the opposite, top-right corner).
 */
function ActiveBadge() {
  return (
    <span className="folder-tile__active-badge" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}